import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, NavigateOptions } from 'react-router'

import routes from '../routes'

interface NavigationFunctions<PARAMS> {
  url(params: PARAMS): string
  goto(params: PARAMS, options?: NavigateOptions): void
  withQuery(...args: any[]): NavigationFunctions<PARAMS>
  withLanguage(lang: string): NavigationFunctions<PARAMS>
}

function navigationFunctions(navigate: any, lang: string, url: (params: any) => string, queryParams: any[] = []) {
  const makeUrl = (params: any) => {
    const searchParams = new URLSearchParams()
    for (const param of queryParams || []) {
      if (param instanceof HTMLFormElement) {
        const formData = new FormData(param)
        for (const key of formData.keys()) {
          searchParams.delete(key)
        }
        for (const [key, value] of formData.entries()) {
          searchParams.append(key, value as any)
        }
      } else if (param.toUrlSearchParams) {
        const p = param.toUrlSearchParams()
        for (const key of p.keys()) {
          searchParams.delete(key)
        }
        for (const [key, value] of p.entries()) {
          searchParams.append(key, value as any)
        }
      } else if (typeof param === 'string') {
        const p = new URLSearchParams(param)
        for (const key of p.keys()) {
          searchParams.delete(key)
        }
        for (const [key, value] of p.entries()) {
          searchParams.append(key, value as any)
        }
      } else {
        for (const [key, value] of Object.entries(param)) {
          searchParams.delete(key)
          for (const v of Array.isArray(value) ? value : [value]) {
            if (v) {
              searchParams.append(key, v)
            }
          }
        }
      }
    }

    return `/${(lang + url(params)).replace(/\/$/, '')}${searchParams.size ? '?' + searchParams.toString() : ''}`
  }

  return {
    url: (args: any) => makeUrl(args),
    goto: (args: any, options: any) => navigate(makeUrl(args), options),
    withQuery: (...newQueryParams: any[]) =>
      navigationFunctions(navigate, lang, url, [...queryParams, ...newQueryParams]),
    withLanguage: (lang: string) => navigationFunctions(navigate, lang, url, queryParams),
  }
}

type NAVITYPE = {
  current: NavigationFunctions<object>
} & {
  [k in keyof typeof routes]: NavigationFunctions<Parameters<(typeof routes)[k]['url']>[0]>
}

export default function useRoutes() {
  const { i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  return useMemo(
    () =>
      Object.fromEntries([
        [
          'current',
          navigationFunctions(
            navigate,
            i18n.language,
            () => '/' + location.pathname.slice(1).split('/').slice(1).join('/')
          ),
        ],
        ...Object.entries(routes).map(([name, { url }]) => [name, navigationFunctions(navigate, i18n.language, url)]),
      ]) as NAVITYPE,
    [navigate, location, i18n.language]
  )
}
